/**
 * §10.2 — billing a timed resource session. Pure functions so POS, waiter app
 * and the API all compute the same number from the same inputs.
 */
import { PricingUnit } from '@/lib/shared-types';

/**
 * X-12 — `PricingUnit` is a Prisma enum and now lives in `@/lib/shared-types`.
 * Re-exported here because every existing caller imports it from `@/lib/domain`.
 */
export { PricingUnit };

export interface PricingRule {
  unit: PricingUnit;
  price: number;
  durationMinutes?: number | null;
  minMinutes?: number | null;
}

/**
 * Billable seconds accumulated across pause/resume, plus the live segment.
 *
 * CONVENTION — for an ACTIVE session, `pausedAt` holds the start of the CURRENT
 * running segment, re-stamped on every resume; it is NOT "the last time someone
 * paused". `accumulatedSeconds` holds every completed segment.
 *
 * So `resume()` must set `pausedAt = now`. Clearing it instead makes the anchor
 * fall back to `startedAt` and re-bills the whole pause — a 30m play + 20m pause
 * + 10m play bills 5400s instead of 2400s. (Re-anchoring `startedAt` on resume
 * also bills correctly, but destroys the real session start time, so we don't.)
 */
export function billableSeconds(session: {
  accumulatedSeconds: number;
  startedAt?: string | Date | null;
  pausedAt?: string | Date | null;
  endedAt?: string | Date | null;
  status: string;
}, now: Date = new Date()): number {
  if (session.status !== 'ACTIVE' || !session.startedAt) return session.accumulatedSeconds;
  const anchor = session.pausedAt ?? session.startedAt;
  const since = Math.max(0, (now.getTime() - new Date(anchor).getTime()) / 1000);
  return Math.round(session.accumulatedSeconds + since);
}

export function priceForDuration(rule: PricingRule, seconds: number): number {
  const minutes = Math.max(seconds / 60, rule.minMinutes ?? 0);
  switch (rule.unit) {
    case 'PER_MINUTE':
      return round2(Math.ceil(minutes) * rule.price);
    case 'PER_30_MINUTES':
      return round2(Math.ceil(minutes / 30) * rule.price);
    case 'PER_HOUR':
      return round2(Math.ceil(minutes / 60) * rule.price);
    case 'FIXED_DURATION': {
      const block = rule.durationMinutes ?? 60;
      return round2(Math.max(1, Math.ceil(minutes / block)) * rule.price);
    }
    case 'FIXED_PRICE':
      return round2(rule.price);
  }
}

/** Picks the highest-priority rule whose day/time window covers `at`. */
export function selectRule<T extends PricingRule & {
  daysOfWeek?: number[];
  startTime?: string | null;
  endTime?: string | null;
  priority?: number;
  isActive?: boolean;
}>(rules: T[], at: Date = new Date()): T | undefined {
  const day = at.getDay();
  const hhmm = `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
  return rules
    .filter((r) => r.isActive !== false)
    .filter((r) => !r.daysOfWeek?.length || r.daysOfWeek.includes(day))
    .filter((r) => !r.startTime || !r.endTime || (hhmm >= r.startTime && hhmm <= r.endTime))
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))[0];
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
